import { distance2D, type AbilityConfig, type EnemyState } from "@mucki/shared";

export interface TargetCandidate {
  id: string;
  x: number;
  z: number;
  state: EnemyState;
}

/**
 * Targeting is the one combat input the client owns (KTD11): the nearest living
 * enemy inside a forward cone within the ability's range. The server re-checks
 * existence, liveness, and range, so naming a different enemy gains nothing.
 */
export function selectTarget(
  origin: { x: number; z: number; rotationY: number },
  candidates: Iterable<TargetCandidate>,
  ability: AbilityConfig,
): string | undefined {
  const forwardX = Math.sin(origin.rotationY);
  const forwardZ = Math.cos(origin.rotationY);
  const maxAngle = (ability.targetingArcDegrees * Math.PI) / 180;

  let bestId: string | undefined;
  let bestDistance = ability.range;

  for (const candidate of candidates) {
    if (candidate.state === "dead") continue;

    const distance = distance2D(origin.x, origin.z, candidate.x, candidate.z);
    if (distance > bestDistance || distance === 0) continue;

    const directionX = (candidate.x - origin.x) / distance;
    const directionZ = (candidate.z - origin.z) / distance;
    const dot = forwardX * directionX + forwardZ * directionZ;
    if (Math.acos(Math.min(1, Math.max(-1, dot))) > maxAngle) continue;

    bestId = candidate.id;
    bestDistance = distance;
  }

  return bestId;
}
